import {
  Box,
  Button,
  CardMedia,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
  alpha,
  Fade,
  Slide,
} from "@mui/material";
import React from "react";
import TextFieldNew from "../components/TextFieldNew";
import { TranslateContext } from "../context/TranslateContext";
import { GlobalContext } from "../context/GlobalContext";
import {
  Login,
  PersonAdd,
  Email,
  Lock,
  Person,
  Phone,
  ArrowForward,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

const UserLogin = () => {
  const { data } = React.useContext(TranslateContext);
  const history = useHistory();
  const { hitAxios } = React.useContext(GlobalContext);
  const theme = useTheme();

  const [state, setState] = React.useState({
    isLogin: true,
    email: "",
    password: "",
  });

  async function loginUser() {
    const res = await hitAxios({
      path: "/api/user/login_user",
      post: true,
      admin: false,
      obj: state,
    });
    if (res.data.success) {
      localStorage.setItem(
        process.env.REACT_APP_TOKEN + "_user",
        res.data.token,
      );
      history.push("/user");
      return;
    }
  }

  async function handleSignup() {
    const res = await hitAxios({
      path: "/api/user/signup",
      post: true,
      admin: false,
      obj: state,
    });
    if (res.data.success) {
      setState({ ...state, isLogin: !state.isLogin });
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        background:
          theme.palette.mode === "dark"
            ? `radial-gradient(ellipse at top, ${alpha(
                theme.palette.primary.dark,
                0.3,
              )}, transparent), radial-gradient(ellipse at bottom, ${alpha(
                theme.palette.secondary.dark,
                0.2,
              )}, transparent)`
            : `radial-gradient(ellipse at top, ${alpha(
                theme.palette.primary.light,
                0.15,
              )}, transparent), radial-gradient(ellipse at bottom, ${alpha(
                theme.palette.secondary.light,
                0.1,
              )}, transparent)`,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.02) 50%, transparent 70%)"
              : "linear-gradient(45deg, transparent 30%, rgba(0,0,0,0.02) 50%, transparent 70%)",
          backgroundSize: "200% 200%",
          animation: "shimmer 15s infinite",
        },
        "@keyframes shimmer": {
          "0%": { backgroundPosition: "0% 0%" },
          "50%": { backgroundPosition: "100% 100%" },
          "100%": { backgroundPosition: "0% 0%" },
        },
      }}
    >
      {/* Animated Background Orbs */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          right: "5%",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(
            theme.palette.primary.main,
            0.15,
          )}, transparent 70%)`,
          filter: "blur(80px)",
          animation: "float 20s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": { transform: "translate(0, 0) scale(1)" },
            "50%": { transform: "translate(-50px, -50px) scale(1.1)" },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "5%",
          left: "10%",
          width: 350,
          height: 350,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(
            theme.palette.secondary.main,
            0.12,
          )}, transparent 70%)`,
          filter: "blur(70px)",
          animation: "float 18s ease-in-out infinite reverse",
        }}
      />

      <Container maxWidth="lg">
        <Grid container spacing={4} justifyContent="center" alignItems="center">
          {/* Left Side - Branding (Hidden on mobile) */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            <Fade in timeout={1000}>
              <Stack spacing={4} alignItems="center">
                <Box
                  sx={{
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: -20,
                      borderRadius: "50%",
                      background: `conic-gradient(from 180deg, ${alpha(
                        theme.palette.primary.main,
                        0.3,
                      )}, transparent, ${alpha(
                        theme.palette.secondary.main,
                        0.3,
                      )})`,
                      animation: "rotate 10s linear infinite",
                    },
                    "@keyframes rotate": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    src="/assets/logo.png"
                    sx={{
                      height: 150,
                      width: 150,
                      borderRadius: "50%",
                      position: "relative",
                      zIndex: 1,
                      boxShadow: `0 20px 60px ${alpha(
                        theme.palette.primary.main,
                        0.4,
                      )}`,
                    }}
                  />
                </Box>
                <Typography
                  variant="h3"
                  fontWeight={800}
                  align="center"
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Welcome Back
                </Typography>
                <Typography
                  variant="h6"
                  align="center"
                  color="text.secondary"
                  sx={{ maxWidth: 400, lineHeight: 1.6 }}
                >
                  {data.helloFromTheDev}
                </Typography>
              </Stack>
            </Fade>
          </Grid>

          {/* Right Side - Form */}
          <Grid item xs={12} md={6} lg={5}>
            <Slide direction="left" in timeout={800}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 5 },
                  borderRadius: 5,
                  backdropFilter: "blur(20px)",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.background.paper, 0.7)
                      : alpha(theme.palette.background.paper, 0.85),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: `0 20px 80px ${alpha(
                    theme.palette.common.black,
                    theme.palette.mode === "dark" ? 0.5 : 0.08,
                  )}`,
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  },
                }}
              >
                <Stack direction="column" spacing={3}>
                  {/* Mobile Logo */}
                  <Stack
                    alignItems="center"
                    mb={1}
                    sx={{ display: { xs: "flex", md: "none" } }}
                  >
                    <CardMedia
                      component="img"
                      src="/assets/logo.png"
                      sx={{
                        height: 70,
                        width: 70,
                        borderRadius: 3,
                        boxShadow: `0 8px 24px ${alpha(
                          theme.palette.primary.main,
                          0.3,
                        )}`,
                      }}
                    />
                  </Stack>

                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      color="text.primary"
                      gutterBottom
                    >
                      {state.isLogin ? data.welcomeBack : data.createAccount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {state.isLogin
                        ? "Enter your credentials to continue"
                        : "Fill in your details to get started"}
                    </Typography>
                  </Box>

                  {state.isLogin ? (
                    <Stack spacing={2.5}>
                      <TextFieldNew
                        value={state.email}
                        onChange={(e) =>
                          setState({ ...state, email: e.target.value })
                        }
                        label={data.email}
                        fullWidth
                        startIcon={<Email color="primary" />}
                      />
                      <TextFieldNew
                        value={state.password}
                        onChange={(e) =>
                          setState({ ...state, password: e.target.value })
                        }
                        label={data.password}
                        type="password"
                        fullWidth
                        startIcon={<Lock color="primary" />}
                      />
                      <Button
                        onClick={loginUser}
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForward />}
                        fullWidth
                        sx={{
                          py: 1.8,
                          mt: 2,
                          borderRadius: 3,
                          textTransform: "none",
                          fontSize: "1rem",
                          fontWeight: 600,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          boxShadow: `0 8px 24px ${alpha(
                            theme.palette.primary.main,
                            0.4,
                          )}`,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: `0 12px 32px ${alpha(
                              theme.palette.primary.main,
                              0.5,
                            )}`,
                          },
                        }}
                      >
                        {data.login}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack spacing={2.5}>
                      <TextFieldNew
                        value={state.email}
                        onChange={(e) =>
                          setState({ ...state, email: e.target.value })
                        }
                        type="email"
                        label={data.email}
                        fullWidth
                        startIcon={<Email color="primary" />}
                      />

                      <TextFieldNew
                        value={state.password}
                        onChange={(e) =>
                          setState({ ...state, password: e.target.value })
                        }
                        label={data.password}
                        type="password"
                        fullWidth
                        startIcon={<Lock color="primary" />}
                      />

                      <Button
                        onClick={handleSignup}
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForward />}
                        fullWidth
                      >
                        {data.signup}
                      </Button>
                    </Stack>
                  )}

                  <Box textAlign="center" pt={2}>
                    <Typography
                      onClick={() =>
                        setState({ ...state, isLogin: !state.isLogin })
                      }
                      sx={{
                        cursor: "pointer",
                        color: "text.secondary",
                        fontWeight: 500,
                        transition: "color 0.2s",
                        "&:hover": {
                          color: "primary.main",
                        },
                      }}
                      variant="body2"
                    >
                      {state.isLogin ? (
                        <>
                          {data.needAccount}{" "}
                          <Box
                            component="span"
                            sx={{ color: "primary.main", fontWeight: 600 }}
                          >
                            Sign up
                          </Box>
                        </>
                      ) : (
                        <>
                          {data.alreadyHaveAccount}{" "}
                          <Box
                            component="span"
                            sx={{ color: "primary.main", fontWeight: 600 }}
                          >
                            Login
                          </Box>
                        </>
                      )}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Slide>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default UserLogin;
